import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStaff } from '@/stores/staff';
import { useLocations } from '@/stores/locations';
import { useHasPayRates, useRole } from '@/stores/identity';
import {
  EMPLOYMENT_TYPES,
  HOURS_TARGET_PERIODS,
  SELECTABLE_JOB_TYPES,
  type EmploymentType,
  type HoursTargetPeriod,
  type JobType,
} from '@/lib/types';
import {
  formatAsTyping,
  isValidNANP,
  PHONE_VALIDATION_HINT,
} from '@/lib/phoneNumber';
import { isValidEmail, suggestEmailFix } from '@/lib/emailValidation';
import { notify } from '@/lib/notify';
import {
  createInvite,
  inviteAcceptUrl,
  sendInviteEmail,
  type InviteEmailResult,
} from '@/lib/staff';
import {
  CUSTOM_INITIALS_REGEX,
  displayInitialsForStaff,
  formatInitialsForChip,
  staffInitials,
} from '@/lib/scheduling';

/**
 * Add a new staff record. Owner-only. After save, lands on the staff detail
 * where the owner can send an invite.
 */
export default function StaffNew() {
  const navigate = useNavigate();
  const role = useRole();
  const hasPayRates = useHasPayRates();
  const add = useStaff((s) => s.add);

  const locations = useLocations((s) => s.items);
  const locStatus = useLocations((s) => s.status);
  const loadLocations = useLocations((s) => s.load);

  // Existing staff in the org -- used to warn on display-name collisions before
  // creating a duplicate row. Soft warning only; owner can still proceed if
  // there really are two real people with the same name.
  const existingStaff = useStaff((s) => s.items);
  const staffStatus = useStaff((s) => s.status);
  const loadStaff = useStaff((s) => s.load);

  useEffect(() => {
    if (locStatus === 'idle') void loadLocations();
  }, [locStatus, loadLocations]);

  useEffect(() => {
    if (staffStatus === 'idle') void loadStaff();
  }, [staffStatus, loadStaff]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobType, setJobType] = useState<JobType>('pharmacy_assistant');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [primaryLocationId, setPrimaryLocationId] = useState<string>('');
  const [minHours, setMinHours] = useState<number>(0);
  const [maxHours, setMaxHours] = useState<number>(40);
  const [hoursTargetPeriod, setHoursTargetPeriod] =
    useState<HoursTargetPeriod>('week');
  const [hourlyRate, setHourlyRate] = useState<string>('');
  // 0109: preferred display initials for calendar chip + avatar. Blank =
  // auto-derive from name. Server normalizes (trim + upper) and CHECK
  // constraint enforces format; we mirror that regex client-side for
  // instant feedback.
  const [customInitials, setCustomInitials] = useState<string>('');
  const [initialsError, setInitialsError] = useState<string | null>(null);
  // Migration 0096: surface active_from for new hires. Default = today so
  // the common case ("start them now") takes zero extra clicks. Override
  // to a future date for delayed-start hires.
  const todayStr = new Date().toISOString().slice(0, 10);
  const [activeFrom, setActiveFrom] = useState<string>(todayStr);
  const [sendInvite, setSendInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (role !== 'owner') {
    return (
      <div className="container mx-auto py-10 px-4 text-sm text-muted-foreground">
        Only owners can add staff.{' '}
        <Link to="/staff" className="underline">
          Back to staff list
        </Link>
      </div>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (maxHours < minHours) {
        throw new Error('Maximum hours must be greater than or equal to minimum.');
      }
      const trimmedEmail = email.trim();
      const trimmedPhone = phone.trim();
      if (trimmedEmail && !isValidEmail(trimmedEmail)) {
        throw new Error('Please enter a valid email address before saving.');
      }
      if (trimmedPhone && !isValidNANP(trimmedPhone)) {
        throw new Error(PHONE_VALIDATION_HINT);
      }
      if (sendInvite) {
        if (!trimmedEmail) {
          throw new Error('Email is required to send an invite. Either fill in the email or uncheck "Also send an invite now".');
        }
        if (!isValidEmail(trimmedEmail)) {
          throw new Error('A valid email address is required to send an invite.');
        }
      }
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (!fullName) {
        throw new Error('Please enter a first and last name.');
      }

      // Soft duplicate-name warning. Case-insensitive, whitespace-normalised
      // match against the existing org roster (active + inactive). Owner can
      // still proceed if there really are two real people with the same name.
      const normalised = fullName.toLowerCase().replace(/\s+/g, ' ');
      const collision = existingStaff.find(
        (s) => (s.display_name ?? '').toLowerCase().replace(/\s+/g, ' ') === normalised
      );
      if (collision) {
        const ok = window.confirm(
          `There's already a staff member named "${collision.display_name}" in your org. ` +
            `Continue creating another one? (Click Cancel if you meant to edit the existing record instead.)`
        );
        if (!ok) {
          setSubmitting(false);
          return;
        }
      }

      const normalisedCustomInitials = customInitials.trim().toUpperCase();
      if (normalisedCustomInitials && !CUSTOM_INITIALS_REGEX.test(normalisedCustomInitials)) {
        throw new Error('Initials must be 1-4 uppercase letters (dots and hyphens allowed).');
      }

      const created = await add({
        display_name: fullName,
        job_type: jobType,
        employment_type: employmentType,
        email: trimmedEmail || null,
        phone: trimmedPhone || null,
        primary_location_id: primaryLocationId || null,
        location_ids: primaryLocationId ? [primaryLocationId] : [],
        min_hours: minHours,
        max_hours: maxHours,
        hours_target_period: hoursTargetPeriod,
        hourly_rate:
          hasPayRates && hourlyRate.trim() !== ''
            ? Number(hourlyRate)
            : null,
        active_from: activeFrom,
        custom_initials: normalisedCustomInitials || null,
      });

      // Optional: create the invite immediately AND auto-send the email.
      // Both results are forwarded to StaffDetail via route state so the
      // InvitePanel can render the green "Invite link ready" box and the
      // "Email sent / send failed" status on first paint.
      let inviteLink:
        | {
            url: string;
            expires_at: string;
            emailResult?: InviteEmailResult;
            recipientEmail?: string;
          }
        | null = null;
      if (sendInvite) {
        try {
          const result = await createInvite({
            staff_id: created.id,
            // Invites always grant 'staff' role. Owners promote to manager
            // later via the Promote action on the staff detail page.
            app_role: 'staff',
            email: trimmedEmail,
            location_ids: primaryLocationId ? [primaryLocationId] : [],
          });
          // Auto-send via SES — never throws; returns a result object.
          const emailResult = await sendInviteEmail(
            result.invite_id,
            result.raw_token
          );
          inviteLink = {
            url: inviteAcceptUrl(result.raw_token),
            expires_at: result.expires_at,
            emailResult,
            recipientEmail: trimmedEmail,
          };
        } catch (err) {
          // Staff was saved successfully; only the invite step failed.
          // Take the user to the detail page with a message so they can retry
          // from the InvitePanel rather than losing the staff record.
          navigate(`/staff/${created.id}`, {
            state: {
              inviteError:
                'Staff saved, but creating the invite failed: ' +
                (err as Error).message +
                '. You can try again from the App access section below.',
            },
          });
          return;
        }
      }

      notify.success(sendInvite && inviteLink ? 'Staff added and invite sent' : 'Staff added');
      navigate(`/staff/${created.id}`, {
        state: inviteLink ? { justCreatedInvite: inviteLink } : undefined,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link to="/staff" className="text-sm text-muted-foreground underline">
          ← All staff
        </Link>
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Add staff</h1>
          <p className="text-sm text-muted-foreground">
            Create the employment record. Once saved, you can send an invite
            from the staff page so they can sign in.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-lg border border-border bg-card p-6 space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name" htmlFor="first_name" required>
              <input
                id="first_name"
                type="text"
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Last name" htmlFor="last_name" required>
              <input
                id="last_name"
                type="text"
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>

          <InitialsField
            firstName={firstName}
            lastName={lastName}
            value={customInitials}
            onChange={(v) => { setCustomInitials(v); if (initialsError) setInitialsError(null); }}
            error={initialsError}
            setError={setInitialsError}
            existingStaff={existingStaff}
          />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Job type" htmlFor="job_type" required>
              <select
                id="job_type"
                required
                value={jobType}
                onChange={(e) => setJobType(e.target.value as JobType)}
                className={inputClass}
              >
                {SELECTABLE_JOB_TYPES.map((j) => (
                  <option key={j.value} value={j.value}>
                    {j.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Employment type" htmlFor="employment_type" required>
              <select
                id="employment_type"
                required
                value={employmentType}
                onChange={(e) =>
                  setEmploymentType(e.target.value as EmploymentType)
                }
                className={inputClass}
              >
                {EMPLOYMENT_TYPES.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <fieldset className="rounded-md border border-border p-4 space-y-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Expected hours
            </legend>
            <p className="text-xs text-muted-foreground">
              How many hours should this person be scheduled? Used to flag
              over- or under-utilization on the reports page. Defaults: 0
              minimum / 40 maximum per week.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Minimum" htmlFor="min_hours">
                <input
                  id="min_hours"
                  type="number"
                  min={0}
                  step="0.5"
                  value={minHours}
                  onChange={(e) => setMinHours(Number(e.target.value))}
                  className={inputClass}
                />
              </Field>
              <Field label="Maximum" htmlFor="max_hours">
                <input
                  id="max_hours"
                  type="number"
                  min={0}
                  step="0.5"
                  value={maxHours}
                  onChange={(e) => setMaxHours(Number(e.target.value))}
                  className={inputClass}
                />
              </Field>
              <Field label="Period" htmlFor="hours_target_period">
                <select
                  id="hours_target_period"
                  value={hoursTargetPeriod}
                  onChange={(e) =>
                    setHoursTargetPeriod(e.target.value as HoursTargetPeriod)
                  }
                  className={inputClass}
                >
                  {HOURS_TARGET_PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </fieldset>

          <fieldset className="rounded-md border border-border p-4 space-y-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Pay rate
            </legend>
            {hasPayRates ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Hourly rate in CAD. Visible only to owners. Drives the
                  Labor cost report. Leave blank if not yet decided.
                </p>
                <Field label="Hourly rate (CAD)" htmlFor="hourly_rate">
                  <input
                    id="hourly_rate"
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. 28.50 (CAD per hour)"
                  />
                </Field>
              </>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  {/* lock icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Pay rate is locked</span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">
                      Pro plan
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Set hourly rates and unlock the Labor cost tile on the
                    Owner Reports page when you upgrade to the Pro or
                    Enterprise plan.
                  </p>
                  <Link
                    to="/billing"
                    className="inline-block text-xs font-medium text-primary hover:underline"
                  >
                    Upgrade to Pro →
                  </Link>
                </div>
              </div>
            )}
          </fieldset>

          <Field
            label="Primary location"
            htmlFor="primary_location"
            hint="Where they work most. Leave blank if not yet decided."
          >
            <select
              id="primary_location"
              value={primaryLocationId}
              onChange={(e) => setPrimaryLocationId(e.target.value)}
              className={inputClass}
            >
              <option value="">— none —</option>
              {locations
                .filter((l) => l.status !== 'closed')
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </Field>

          {/* Migration 0096: surface active_from on create. Defaults to
              today; override for a future start date (e.g. new hire in
              two weeks). active_to is intentionally not shown here --
              new staff start with no end date, set it later if needed. */}
          <Field
            label="Active from"
            htmlFor="active_from"
            hint="When this person's tenure starts. Defaults to today."
          >
            <input
              id="active_from"
              type="date"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
              className={inputClass}
              required
            />
          </Field>

          <Field label="Email (contact)" htmlFor="email" hint="Used as the invite address.">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
                if (emailSuggestion) setEmailSuggestion(null);
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val && !isValidEmail(val)) {
                  setEmailError('Please enter a valid email address.');
                  setEmailSuggestion(null);
                } else {
                  setEmailError(null);
                  setEmailSuggestion(suggestEmailFix(val));
                }
              }}
              className={inputClass}
            />
            {emailError && (
              <p className="text-xs text-destructive mt-1">{emailError}</p>
            )}
            {emailSuggestion && !emailError && (
              <p className="text-xs text-muted-foreground mt-1">
                Did you mean{' '}
                <button
                  type="button"
                  className="underline text-primary"
                  onClick={() => {
                    setEmail(emailSuggestion);
                    setEmailSuggestion(null);
                  }}
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </Field>

          <fieldset className="rounded-md border border-border p-4 space-y-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              App access (optional)
            </legend>
            <p className="text-xs text-muted-foreground">
              Tick this to also email a one-time invite link to this person as
              soon as the staff record is saved. The link is also shown on the
              next page so you can copy it for SMS or share it manually if the
              email fails to deliver.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendInvite}
                disabled={!email.trim() || !!emailError}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border border-border"
              />
              <span>
                Also send an invite now
                {(!email.trim() || emailError) && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (enter a valid email above first)
                  </span>
                )}
              </span>
            </label>
            {sendInvite && (
              <p className="text-xs text-muted-foreground">
                Invites always grant the <strong>Staff</strong> role. If this
                person should be a pharmacy manager, promote them from their
                staff page after they accept the invite.
              </p>
            )}
          </fieldset>

          <Field label="Phone" htmlFor="phone">
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(formatAsTyping(e.target.value));
                if (phoneError) setPhoneError(null);
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val && !isValidNANP(val)) {
                  setPhoneError(PHONE_VALIDATION_HINT);
                } else {
                  setPhoneError(null);
                }
              }}
              className={inputClass}
              placeholder="(416) 555-1234"
            />
            {phoneError && (
              <p className="text-xs text-destructive mt-1">{phoneError}</p>
            )}
          </Field>

          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Link
              to="/staff"
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted/40"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting
                ? sendInvite
                  ? 'Saving and creating invite...'
                  : 'Saving...'
                : sendInvite
                ? 'Add staff and create invite link'
                : 'Add staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * 0109: initials input with live preview of the calendar-chip render
 * and an org-scoped collision warning. Save-allowed even on collision;
 * the warning just calls it out so the owner can decide.
 */
function InitialsField({
  firstName,
  lastName,
  value,
  onChange,
  error,
  setError,
  existingStaff,
}: {
  firstName: string;
  lastName: string;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  setError: (s: string | null) => void;
  existingStaff: { id: string; display_name: string; custom_initials?: string | null }[];
}) {
  const trimmed = value.trim().toUpperCase();
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  // The effective value used for the preview + collision check. If the
  // user has typed something, use it; otherwise derive from the name
  // entered so far.
  const effective = trimmed
    ? trimmed
    : fullName
      ? staffInitials(fullName)
      : '';
  const chipRender = effective ? formatInitialsForChip(effective) : '';

  // Collision detection: any existing org staff whose EFFECTIVE initials
  // (custom OR derived) match the value we would save for this new person.
  // Case-insensitive.
  const collisions = effective
    ? existingStaff.filter(
        (s) =>
          displayInitialsForStaff(s).toUpperCase() === effective.toUpperCase()
      )
    : [];

  return (
    <div className="rounded-md border border-border p-4 space-y-2">
      <label htmlFor="custom_initials" className="block text-sm font-medium">
        Preferred initials (optional)
      </label>
      <p className="text-xs text-muted-foreground">
        Shown on the calendar shift chip and the sidebar avatar. Leave blank to
        auto-derive from the name (for example, &ldquo;Sarah AlKhafaji&rdquo; →{' '}
        <span className="font-mono">S.A.</span>). 1&ndash;4 uppercase letters;
        dots and hyphens allowed.
      </p>
      <div className="flex items-center gap-3">
        <input
          id="custom_initials"
          type="text"
          maxLength={4}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onBlur={() => {
            const v = value.trim().toUpperCase();
            if (v && !CUSTOM_INITIALS_REGEX.test(v)) {
              setError('1-4 uppercase letters, dots, or hyphens.');
            } else {
              setError(null);
            }
          }}
          placeholder={fullName ? staffInitials(fullName) : 'e.g. SAK'}
          className={inputClass + ' font-mono uppercase'}
          style={{ maxWidth: '160px' }}
          autoComplete="off"
        />
        {chipRender && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Preview:</span>
            <span className="inline-block rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-sm tabular-nums">
              {chipRender}
            </span>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!error && collisions.length > 0 && (
        <p className="text-xs text-amber-700">
          <strong>Heads up:</strong> {collisions.length === 1
            ? `${collisions[0].display_name} already uses these initials.`
            : `${collisions[0].display_name} and ${collisions.length - 1} other${collisions.length === 2 ? '' : 's'} already use these initials.`}{' '}
          Save is allowed &mdash; consider a distinct value (e.g. &ldquo;SAK&rdquo;) to
          avoid confusion on the calendar.
        </p>
      )}
    </div>
  );
}
